import { Info } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * An icon that reveals help text on hover, focus, or tap, instead of a paragraph
 * that is always on screen. Keeps space-hungry explanations available without
 * competing with primary content for attention.
 */
export function InfoTip({ label = 'More information', children }: { label?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <span className="relative inline-flex" ref={rootRef}>
      {/* The icon itself stays 20px so it sits inline with heading text at the same visual weight
          as before; the button's own box grows to a 44px tap target and the matching negative
          margin cancels that growth back out of the surrounding inline layout, so nothing next to
          it shifts or wraps differently. */}
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="-m-3 grid size-11 shrink-0 place-items-center rounded-full text-ink/45 hover:text-burgundy focus-visible:text-burgundy"
      >
        <Info className="size-4" aria-hidden="true" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-control border border-border bg-surface p-3 text-left text-xs font-normal leading-5 text-ink/80 shadow-card"
        >
          {children}
        </span>
      )}
    </span>
  );
}
